import React, { useCallback } from "react";
import DadosBasicosOutrosProponentesFintechsForm from "./DadosBasicosOutrosProponentesFintechsForm";
import { useDispatch, useSelector } from "react-redux";
import {
  getPreenchimentoOperacao,
  isBuscandoPreenchimentoOperacao,
  isGravandoPreenchimentoOperacao,
} from "../../../../../selectors/operacao.selectors";
import useCompleteForm from "../../../../../hooks/useCompleteForm";
import validators from "../../../../../utils/validators";
import { actions } from "../../../../../actions/operacao.actions";
import {
  removeNonDigitsFromString,
  transformToDate,
} from "../../../../../utils/basic";
import formatters from "../../../../../utils/formatters";

const DadosBasicosOutrosProponentesFintechsFormConnected = ({
  setStep,
  eventsOutrosPf,
  eventsOutrosPj,
  participante,
  setParticipante,
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const {
    operacao,
    dominios: { qualificacao: qualificacoes, tipoEmpresa: tiposEmpresa },
  } = useSelector(getPreenchimentoOperacao);
  const submitting = useSelector(isGravandoPreenchimentoOperacao);
  const searching = useSelector(isBuscandoPreenchimentoOperacao);

  const callback = useCallback(
    (data) => {
      const c = data.operacao.participantes.find(
        (p) => p.id === participante.id
      );
      if (!!c) {
        setParticipante({
          ...c,
          fluxo: data.validacao.completa.participantes.find(
            (f) => f.id === c?.id
          )?.etapas,
        });
      }
      setStep(51);
    },
    [setStep, setParticipante, participante]
  );

  const [formProps, handleSubmit] = useCompleteForm({
    rules: () => ({
      cpfCnpj: validators.cpfCnpj({
        required: false,
        custom: {
          obrigatorio: (value) => {
            const c = operacao.participantes.find(
              (p) => p.id === participante.id
            );
            if (!!c.cpfCnpj) return true;
            if (value !== undefined && value !== null && value !== "") {
              return true;
            }
            return "Campo obrigatório";
          },
        },
      }),
      nome: validators.string({
        required: true,
        maxLength: 100,
        custom: {
          nomeCompleto: (value) =>
            !value ||
            (value.indexOf(" ") > 0 &&
              value.substring(value.indexOf(" ") + 1).length > 0) ||
            "Nome informado deve ser completo",
        },
      }),
      dataNascimento: validators.date({
        required: participante.tipoPessoa === "F",
        min: "01/01/1900",
        max: formatters.dates.short(new Date()),
      }),
      email: validators.email({ required: true }),
      telefone: validators.phone({ required: true }),
      tipoEmpresa: validators.object({
        required: participante.tipoPessoa === "J",
      }),
      qualificacao: validators.object({ required: false }),
    }),
    initialValues: useCallback(
      () => ({
        cpfCnpj: "",
        nome: "",
        dataNascimento: "",
        email: "",
        telefone: "",
        qualificacao: null,
        tipoEmpresa: null,
        ...participante,
      }),
      [participante]
    ),
    handleSubmit: useCallback(
      (values) => {
        dispatch(
          actions.gravarPreenchimento.request({
            callback,
            instituicao: operacao.codigoInstituicao,
            passo: "dados-pessoais-outros-proponentes",
            participante: {
              id: participante?.id,
              cpfCnpj: removeNonDigitsFromString(values.cpfCnpj),
              nomeParticipante: values.nome,
              dataNascimento:
                participante.tipoPessoa === "F"
                  ? transformToDate(values.dataNascimento)
                  : null,
              email: values.email,
              telefone: values.telefone,
              idTipoEmpresa:
                participante.tipoPessoa === "J" ? values.tipoEmpresa?.id : null,
              idQualificacao: values.qualificacao?.id,
            },
            operacao: {
              id: operacao.id,
            },
          })
        );
      },
      [dispatch, participante, operacao, callback]
    ),
  });

  const { reset } = formProps;

  const successCallback = useCallback(
    (data) => {
      if (!!data) {
        const c = data.operacao.participantes.find(
          (p) => p.id === participante.id
        );
        if (!!c) {
          setParticipante({
            ...c,
            fluxo: data.validacao.completa.participantes.find(
              (f) => f.id === c?.id
            )?.etapas,
          });
          reset({
            cpfCnpj: "",
            nome: "",
            dataNascimento: "",
            email: "",
            telefone: "",
            qualificacao: null,
            tipoEmpresa: null,
            ...c,
          });
        }
      }
    },
    [setParticipante, participante, reset]
  );

  const handleCpfCnpjChange = useCallback(
    (v) => {
      if (!!v) {
        dispatch(
          actions.atualizarParticipantePreenchimento.request({
            id: participante.id,
            cpfCnpj: removeNonDigitsFromString(v),
            tipo: "preenchimento",
            callback: successCallback,
          })
        );
      }
    },
    [dispatch, participante, successCallback]
  );

  return (
    <DadosBasicosOutrosProponentesFintechsForm
      {...otherProps}
      tipoPessoa={participante.tipoPessoa}
      tiposEmpresa={tiposEmpresa}
      qualificacoes={qualificacoes}
      formProps={formProps}
      handleSubmit={handleSubmit}
      submitting={submitting}
      handleCpfCnpjChange={handleCpfCnpjChange}
      searching={searching}
      cpfCnpjDisabled={!!participante.cpfCnpj}
      fluxo={participante.fluxo}
      events={participante.tipoPessoa === "F" ? eventsOutrosPf : eventsOutrosPj}
    />
  );
};

export default DadosBasicosOutrosProponentesFintechsFormConnected;
